// src/components/staff/StaffDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import { 
  FiCalendar, FiUsers, FiDollarSign, FiTrendingUp, 
  FiClock, FiStar, FiActivity, FiTarget 
} from 'react-icons/fi';

// Helper function to calculate revenue from compensation plan
const calculateRevenueFromPlan = (invoices, appointments, compensationPlan, clinicSettings) => {
  if (!compensationPlan || !invoices.length) return 0;

  let totalRevenue = 0;

  invoices.forEach(invoice => {
    const invoiceAmount = parseFloat(invoice.amount) || 0;
    
    // Find the related appointment to get treatment type
    const relatedAppointment = appointments.find(apt => apt.id === invoice.appointmentId);
    const treatmentType = relatedAppointment?.type || 'General';
    
    // Calculate based on compensation plan structure
    if (compensationPlan.commissionBasis === 'percentage') {
      const percentage = compensationPlan.commissionRate || 0;
      totalRevenue += invoiceAmount * (percentage / 100);
    } else if (compensationPlan.commissionBasis === 'flat') {
      totalRevenue += compensationPlan.commissionRate || 0;
    } else if (compensationPlan.commissionBasis === 'treatment') {
      // Use treatment-specific rates from clinic settings
      const treatmentRate = clinicSettings?.treatmentRates?.[treatmentType];
      if (treatmentRate) {
        if (treatmentRate.type === 'hourly') {
          // Assume 1 hour per appointment for now
          totalRevenue += treatmentRate.value;
        } else {
          totalRevenue += treatmentRate.value;
        }
      }
    }
  });

  return totalRevenue;
};

const StaffDashboard = () => {
  const { userProfile } = useAuth();
  const { currentTenant } = useTenant();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('thisMonth');

  useEffect(() => {
    loadDashboardData();
  }, [userProfile?.uid, currentTenant?.id, selectedPeriod]);

  const loadDashboardData = async () => {
    if (!userProfile?.uid || !currentTenant?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      tenantDataService.setTenantContext(currentTenant.id);

      // Load all required data
      const [appointments, invoices, clinicSettings, compensationPlans] = await Promise.all([
        tenantDataService.getAll('appointments'),
        tenantDataService.getAll('invoices'),
        tenantDataService.getById('settings', 'clinic'),
        tenantDataService.getAll('compensationPlans')
      ]);

      console.log('📊 StaffDashboard: Loaded appointments:', appointments.length);
      console.log('📊 StaffDashboard: Loaded invoices:', invoices.length);
      console.log('📊 StaffDashboard: Clinic settings:', clinicSettings);
      console.log('📊 StaffDashboard: Current user ID:', userProfile.uid);

      const staffAppointments = appointments.filter(apt => {
        const isStaffMatch = apt.staffId === userProfile.uid;
        console.log('📊 Appointment check:', apt.id, 'staffId:', apt.staffId, 'matches:', isStaffMatch);
        return isStaffMatch;
      });

      console.log('📊 StaffDashboard: Filtered staff appointments:', staffAppointments.length);

      // Calculate date ranges
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 6));

      let filteredAppointments = staffAppointments;
      if (selectedPeriod === 'thisWeek') {
        filteredAppointments = staffAppointments.filter(apt => {
          const aptDate = new Date(apt.date);
          return aptDate >= startOfWeek && aptDate <= endOfWeek;
        });
      } else if (selectedPeriod === 'thisMonth') {
        filteredAppointments = staffAppointments.filter(apt => {
          const aptDate = new Date(apt.date);
          return aptDate >= startOfMonth && aptDate <= endOfMonth;
        });
      }

      console.log('📊 StaffDashboard: Filtered appointments for period:', filteredAppointments.length);

      // Calculate statistics
      const totalAppointments = filteredAppointments.length;
      const completedAppointmentsList = filteredAppointments.filter(apt => 
        apt.status === 'completed' || apt.status === 'attended'
      );
      const completedAppointmentsCount = completedAppointmentsList.length;
      const cancelledAppointments = filteredAppointments.filter(apt => apt.status === 'cancelled').length;
      const noShowAppointments = filteredAppointments.filter(apt => apt.status === 'no-show' || apt.status === 'dna').length;
      
      // Calculate revenue based on clinic's financial structure
      const completedAppointmentIds = completedAppointmentsList.map(apt => apt.id);

      // Get invoices for completed appointments
      const relatedInvoices = invoices.filter(invoice => 
        completedAppointmentIds.includes(invoice.appointmentId) &&
        invoice.status === 'paid'
      );

      // Get unique clients
      const uniqueClients = new Set(filteredAppointments.map(apt => apt.clientId)).size;

      // Calculate client retention rate
      const clientRetentionRate = uniqueClients > 0 && totalAppointments > 0 
        ? (totalAppointments / uniqueClients) 
        : 0;

      // Calculate appointment types
      const appointmentTypes = {};
      filteredAppointments.forEach(apt => {
        const type = apt.type || 'General';
        appointmentTypes[type] = (appointmentTypes[type] || 0) + 1;
      });

      // Calculate average rating (if feedback exists)
      const appointmentsWithRating = filteredAppointments.filter(apt => apt.rating);
      const averageRating = appointmentsWithRating.length > 0 
        ? appointmentsWithRating.reduce((sum, apt) => sum + apt.rating, 0) / appointmentsWithRating.length 
        : 0;

      setDashboardData({
        totalAppointments,
        completedAppointments: completedAppointmentsCount,
        cancelledAppointments,
        noShowAppointments,
        clientRetentionRate,
        uniqueClients,
        appointmentTypes,
        averageRating,
        completionRate: totalAppointments > 0 ? (completedAppointmentsCount / totalAppointments) * 100 : 0,
        noShowRate: totalAppointments > 0 ? (noShowAppointments / totalAppointments) * 100 : 0
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-lg border p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Performance Dashboard</h2>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="thisWeek">This Week</option>
          <option value="thisMonth">This Month</option>
          <option value="allTime">All Time</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Appointments */}
        <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 font-medium text-sm">Total Appointments</p>
              <p className="text-2xl font-bold text-blue-900">
                {dashboardData?.totalAppointments || 0}
              </p>
            </div>
            <FiCalendar className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        {/* Clients Seen */}
        <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 font-medium text-sm">Clients Seen</p>
              <p className="text-2xl font-bold text-green-900">
                {dashboardData?.uniqueClients || 0}
              </p>
            </div>
            <FiUsers className="w-8 h-8 text-green-500" />
          </div>
        </div>

        {/* Average per Appointment */}
        <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 font-medium text-sm">Client Retention</p>
              <p className="text-2xl font-bold text-purple-900">
                {dashboardData?.clientRetentionRate?.toFixed(1) || '0.0'}x
              </p>
            </div>
            <FiTrendingUp className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        {/* Completion Rate */}
        <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 font-medium text-sm">Completion Rate</p>
              <p className="text-2xl font-bold text-orange-900">
                {dashboardData?.completionRate?.toFixed(1) || 0}%
              </p>
            </div>
            <FiTarget className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Appointment Status Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Completed</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${dashboardData?.completionRate || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">{dashboardData?.completedAppointments || 0}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Cancelled</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full" 
                    style={{ width: `${dashboardData?.totalAppointments > 0 ? (dashboardData.cancelledAppointments / dashboardData.totalAppointments) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">{dashboardData?.cancelledAppointments || 0}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Did Not Attend</span>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full" 
                    style={{ width: `${dashboardData?.noShowRate || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">{dashboardData?.noShowAppointments || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Service Types */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Types</h3>
          <div className="space-y-3">
            {dashboardData?.appointmentTypes && Object.entries(dashboardData.appointmentTypes)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-gray-600">{type}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${(count / dashboardData.totalAppointments) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                </div>
              ))}
            {(!dashboardData?.appointmentTypes || Object.keys(dashboardData.appointmentTypes).length === 0) && (
              <p className="text-gray-500 text-center py-4">No appointment data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* DNA Rate */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <FiActivity className="w-8 h-8 text-orange-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {dashboardData?.noShowRate?.toFixed(1) || 0}%
          </p>
          <p className="text-sm text-gray-600">DNA Rate</p>
        </div>

        {/* Success Rate */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <FiTrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900">
            {dashboardData?.completionRate?.toFixed(0) || 0}%
          </p>
          <p className="text-sm text-gray-600">Success Rate</p>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
